import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { useAppStore } from '../../store/appStore'

interface AgentRun {
  id: string
  goal: string
  status: string
  filesModified: string[]
  createdAt: number
}

export function BackupRestoreButton() {
  const [open, setOpen] = useState(false)
  const [runs, setRuns] = useState<AgentRun[]>([])
  const [loading, setLoading] = useState(false)
  const [restoredIds, setRestoredIds] = useState<Set<string>>(new Set())

  const loadRuns = async () => {
    setLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/agent-stream/runs'))
      if (res.ok) {
        const data = await res.json()
        setRuns(Array.isArray(data) ? data.filter((r: AgentRun) => r.filesModified?.length > 0) : [])
      }
    } catch {}
    setLoading(false)
  }

  const handleRollback = async (runId: string) => {
    try {
      const res = await authFetch(apiUrl('/api/agent-stream/rollback-session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: runId }),
      })
      if (res.ok) {
        const data = await res.json()
        setRestoredIds(prev => new Set([...prev, runId]))
        // Refresh open editor tabs
        for (const filePath of data.restored ?? []) {
          const storedPath = `/${filePath.replace(/\\/g, '/')}`
          const store = useAppStore.getState()
          const tab = store.openTabs.find(t => t.path === storedPath)
          if (tab) {
            try {
              const r = await authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(filePath)}`))
              if (r.ok) {
                const d = await r.json()
                store.markDirty(tab.id, d.content)
              }
            } catch {}
          }
          store.clearAgentFilePatch(storedPath)
        }
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      }
    } catch {}
  }

  const formatTime = (ts: number) => {
    const d = new Date(ts)
    const now = Date.now()
    const diff = now - ts
    if (diff < 60000) return 'just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return d.toLocaleDateString()
  }

  return (
    <>
      <button
        onClick={() => { setOpen(true); loadRuns() }}
        title="Restore from agent backup"
        style={{
          background: 'none', border: 'none', color: 'var(--text-2)',
          cursor: 'pointer', fontSize: 10, padding: '0 8px',
          fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        ↩ Backups
      </button>

      {open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setOpen(false)}>
          <div style={{
            background: 'var(--bg-1)', borderRadius: 12, padding: 20,
            border: '1px solid var(--border-2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            width: '90%', maxWidth: 500, maxHeight: '70vh', display: 'flex', flexDirection: 'column',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-0)', marginBottom: 4 }}>
              ↩ Agent Backups
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
              Rollback entire agent sessions. Restores all files to before the agent touched them.
            </div>

            <div style={{ flex: 1, overflow: 'auto' }}>
              {loading && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>Loading...</div>}
              {!loading && runs.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>
                  No agent sessions with file changes found.
                </div>
              )}
              {runs.map(run => {
                const isRestored = restoredIds.has(run.id)
                return (
                  <div key={run.id} style={{
                    padding: '10px 12px', borderBottom: '1px solid var(--border)',
                    opacity: isRestored ? 0.5 : 1,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12 }}>🤖</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-0)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {run.goal.slice(0, 60)}{run.goal.length > 60 ? '…' : ''}
                      </span>
                      <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                        {formatTime(run.createdAt)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                      {run.filesModified.map(f => (
                        <span key={f} style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 3,
                          background: 'rgba(124,110,245,0.08)', color: 'var(--accent)',
                          fontFamily: 'var(--font-mono)',
                        }}>
                          {f.split('/').pop()}
                        </span>
                      ))}
                    </div>
                    {isRestored ? (
                      <span style={{ fontSize: 10, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)' }}>✓ Restored</span>
                    ) : (
                      <button onClick={() => handleRollback(run.id)} style={{
                        padding: '4px 12px', borderRadius: 4, border: '1px solid rgba(240,112,112,0.3)',
                        background: 'rgba(240,112,112,0.06)', color: '#f07070',
                        fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)',
                      }}>
                        ↩ Rollback this session
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            <button onClick={() => setOpen(false)} style={{
              marginTop: 12, padding: '8px 16px', borderRadius: 6,
              border: '1px solid var(--border)', background: 'none',
              color: 'var(--text-1)', cursor: 'pointer', fontSize: 11,
              fontFamily: 'var(--font-mono)', width: '100%',
            }}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  )
}
